import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { areaNameFor } from "@/data/postcodeAreas";
import { AnimatedNumber } from "@/components/silver/common/AnimatedNumber";
import { Reveal } from "@/components/silver/common/Reveal";
import { PostcodeSearch } from "@/components/silver/tool/PostcodeSearch";

const LIMIT = 8;

export function GeoBreakdown() {
  const { audience, removeArea } = useSilver();
  const [adding, setAdding] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const rows = [...audience.geography].sort((a, b) => b.households - a.households);
  const visible = showAll ? rows : rows.slice(0, LIMIT);
  const max = Math.max(1, ...rows.map((r) => r.households));

  return (
    <Reveal className="glass p-7 sm:p-10" data-testid="geo-breakdown">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Geographic breakdown</p>
          <h3 className="mt-3 text-3xl font-extrabold tracking-[-0.04em]">Where they live.</h3>
        </div>
        <button type="button" className="btn-ghost !py-2.5 text-[13px]" onClick={() => setAdding((v) => !v)} aria-expanded={adding} data-testid="add-another-area-btn">
          <Plus className="h-4 w-4" /> Add another area
        </button>
      </div>

      <AnimatePresence initial={false}>
        {adding && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="pt-6">
              <PostcodeSearch variant="compact" autoFocus showChips={false} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ul className="mt-8 divide-y divide-white/[0.07]">
        <AnimatePresence initial={false}>
          {visible.map((r) => (
            <motion.li key={r.code} layout initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16, transition: { duration: 0.25 } }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }} className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 py-5 sm:grid-cols-[150px_1fr_auto]" data-testid={`geo-row-${r.code}`}>
              <div>
                <p className="font-ui text-xl font-bold tracking-[0.02em] text-white">{r.code}</p>
                <p className="text-[12px] text-white/45">{areaNameFor(r.code)}</p>
              </div>
              <div className="col-span-2 row-start-2 sm:col-span-1 sm:row-start-auto">
                <div className="h-2.5 overflow-hidden rounded-full bg-white/[0.06]">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#7AA818] to-[#C6EE5A] transition-[width] duration-1000 ease-out" style={{ width: `${(r.households / max) * 100}%` }} />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-right">
                  <AnimatedNumber value={r.households} className="num block text-lg font-extrabold text-white" data-testid={`geo-row-${r.code}-households`} />
                  <span className="font-ui text-[11px] text-white/45">{Math.round(r.share * 100)}% · matching</span>
                </p>
                <button type="button" onClick={() => removeArea(r.code)} aria-label={`Remove ${r.code}`} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-white/50 transition-colors hover:border-[#EF4444]/50 hover:text-white" data-testid={`geo-remove-${r.code}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>
      {rows.length > LIMIT && (
        <button type="button" className="pill mt-5" onClick={() => setShowAll((v) => !v)} data-testid="geo-show-all-btn">
          {showAll ? "Show fewer" : `Show all ${rows.length} districts`}
        </button>
      )}
    </Reveal>
  );
}
